import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";

export interface TableData {
  id: number;
  location_id: number;
  number: string;
  is_booked: boolean;
  qrcode: string;
  slug: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  location: {
    id: number;
    name: string;
    address: string;
    code: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
}

interface TableResponse {
  data: TableData[];
  meta: {
    total: number;
    current_page: number;
    last_page: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

interface TableFilters {
  [key: string]: string | number | boolean | undefined;
  location_id?: string | number;
  search?: string;
}

// Get tables with pagination and filtering (existing)
export const useTables = (page: number = 1, filters: TableFilters = {}) => {
  return useQuery({
    queryKey: ["tables", page, filters],
    queryFn: async () => {
      const filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== null && v !== "")
      );

      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...(filteredFilters as Record<string, string>),
      });

      const response = await apiClient.get<TableResponse>(
        `/table?${queryParams.toString()}`
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

// ✅ Get all tables without pagination (for dropdowns)
export const useAllTables = (filters: TableFilters = {}) => {
  return useQuery<TableData[], Error>({
    queryKey: ["allTables", filters],
    queryFn: async () => {
      const filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== null && v !== "")
      );

      // First get page 1 to see how many pages there are
      const queryParams = new URLSearchParams({
        page: "1",
        ...(filteredFilters as Record<string, string>),
      });

      const firstPageResponse = await apiClient.get<TableResponse>(
        `/table?${queryParams.toString()}`
      );
      const firstPageData = firstPageResponse.data;

      // If there's only one page, return the data
      if (firstPageData.meta.last_page === 1) {
        return firstPageData.data;
      }

      // Otherwise, we need to fetch all pages
      const totalPages = firstPageData.meta.last_page;
      const allTables: TableData[] = [...firstPageData.data];

      // Fetch the remaining pages in parallel
      const pagePromises = [];
      for (let page = 2; page <= totalPages; page++) {
        const pageQueryParams = new URLSearchParams({
          page: page.toString(),
          ...(filteredFilters as Record<string, string>),
        });
        pagePromises.push(
          apiClient.get<TableResponse>(`/table?${pageQueryParams.toString()}`)
        );
      }

      const pageResponses = await Promise.all(pagePromises);

      // Combine the data from all pages
      pageResponses.forEach((response) => {
        allTables.push(...response.data.data);
      });

      return allTables;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    refetchOnWindowFocus: false,
  });
};

// Create a new table
export const useCreateTable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableData: {
      location_id: number;
      is_booked: boolean;
    }) => {
      const response = await apiClient.post("/table/create", tableData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      queryClient.invalidateQueries({ queryKey: ["allTables"] });
    },
    onError: (error) => {
      console.error("Error creating table:", error);
      throw error; // Re-throw to handle in the component
    },
  });
};

// Update an existing table
export const useUpdateTable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableData: {
      id: number;
      location_id: number;
      is_booked: boolean;
    }) => {
      const response = await apiClient.put(`/table/${tableData.id}`, tableData);
      return response.data;
    },
    onSuccess: () => {
      message.success("Table updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      queryClient.invalidateQueries({ queryKey: ["allTables"] });
    },
    onError: (error) => {
      console.error("Error updating table:", error);
      message.error("Failed to update table. Please try again.");
    },
  });
};

// Delete a table
export const useDeleteTable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableId: number) => {
      const response = await apiClient.delete(`/table/${tableId}`);
      return response.data;
    },
    onSuccess: () => {
      message.success("Table deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      queryClient.invalidateQueries({ queryKey: ["allTables"] });
    },
    onError: (error) => {
      console.error("Error deleting table:", error);
      message.error("Failed to delete table. Please try again.");
    },
  });
};

// Bulk delete tables
export const useBulkDeleteTables = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableIds: number[]) => {
      // Delete tables one by one (or you can create a bulk delete API endpoint)
      const deletePromises = tableIds.map((id) =>
        apiClient.delete(`/table/${id}`)
      );

      const responses = await Promise.all(deletePromises);
      return responses;
    },
    onSuccess: (_, tableIds) => {
      message.success(`${tableIds.length} table(s) deleted successfully!`);
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      queryClient.invalidateQueries({ queryKey: ["allTables"] });
    },
    onError: (error) => {
      console.error("Error deleting tables:", error);
      message.error("Failed to delete some tables. Please try again.");
    },
  });
};
